import {
  Badge,
  Button,
  Card,
  Container,
  CopyButton,
  Grid,
  Modal,
  Pagination,
  Paper,
  Table,
  TableThProps,
  Tabs,
  Text,
  TextProps,
  Tooltip,
} from "@mantine/core";

import "./styles.scss";
import { useContext, useState } from "react";
import { AppContext } from "../Wrapper";
import MegaLoader from "../../Components/MegaLoader";
import DisplayAlert from "../../Components/DisplayAlert";
import { data, Link } from "react-router-dom";
import { AppName, BrandImages, Colors } from "../../Lib/Data";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/Responses";
import {
  PaginationProps,
  PayoutRequestProps,
  PromoterLeaderboardRecordProps,
  PromoterProfileProps,
  PromoterTransactionProps,
} from "../../Lib/Types";
import { Endpoints } from "../../Lib/Endpoints";
import {
  IconCurrencyDollar,
  IconHandStop,
  IconReceipt,
  IconTimeline,
  IconUser,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  generateRandomString,
  getFinancialValueFromNumeric,
  getOriginalValueFromDiscount,
  getPaginationTotal,
  maskEmail,
} from "../../Lib/Methods";
import { useDisclosure } from "@mantine/hooks";
import { getValueFromValueOptions } from "@mui/x-data-grid/components/panel/filterPanel/filterPanelUtils";

const PromoterScreen: React.FC = () => {
  const context = useContext(AppContext);
  const theme = context?.theme ?? "light";

  document.title = `Promoter | ${AppName}`;

  const [transactionsPagination, setTransactionsPagination] =
    useState<PaginationProps>({
      page: 1,
      limit: 20,
    });
  const [promotersPagination, setPromotersPagination] =
    useState<PaginationProps>({
      page: 1,
      limit: 20,
    });
  const [payoutsPagination, setPayoutsPagination] = useState<PaginationProps>({
    page: 1,
    limit: 20,
  });
  const {
    data: promoter,
    isLoading: isLoadingPromoter,
    reloadData: reloadPromoterProfile,
  } = usePerformRequest<NonPaginatedResponse<PromoterProfileProps>>({
    method: "GET",
    route: Endpoints.GetPromoterProfile(),
  });
  const {
    data: payouts,
    isLoading: isLoadingPayouts,
    reloadData: reloadPayouts,
  } = usePerformRequest<PaginatedResponse<PayoutRequestProps[]>>({
    method: "POST",
    body: {
      status: "all",
    },
    route: Endpoints.GetPromoterPayoutHistory(
      payoutsPagination.page,
      payoutsPagination.limit
    ),
  });
  const {
    data: leaderboard,
    isLoading: isLoadingLeaderboard,
    reloadData: reloadLeaderboard,
  } = usePerformRequest<PaginatedResponse<PromoterLeaderboardRecordProps[]>>({
    method: "GET",
    route: Endpoints.GetPromotersLeaderboard(
      promotersPagination.page,
      promotersPagination.limit
    ),
  });
  const {
    data: transactions,
    isLoading: isLoadingTransactions,
    reloadData: reloadTransactions,
  } = usePerformRequest<PaginatedResponse<PromoterTransactionProps[]>>({
    method: "GET",
    route: Endpoints.GetPromoterTransactions(
      transactionsPagination.page,
      transactionsPagination.limit
    ),
  });
  const [
    isTransactionModalOpened,
    { open: OpenTransactionModal, close: CloseTransactionModal },
  ] = useDisclosure(false);

  const [currentTransaction, setCurrentTransaction] =
    useState<PromoterTransactionProps>();

  const [isRequestingPayout, setRequestingPayout] = useState<boolean>(false);
  const RequestPayout = async () => {
    setRequestingPayout(true);
    const r = await PerformRequest({
      method: "GET",
      route: Endpoints.GetRequestPayout(),
      callback: () => {
        DefaultErrorNotification("Could not request payout!");
        setRequestingPayout(false);
      },
    });
    setRequestingPayout(false);
    if (r) {
      if (r.status === 201) {
        DefaultSuccessNotification("Payout request sent successfully!");
        reloadPayouts();
      } else {
        DefaultErrorNotification("Could not request payout!");
      }
    }
  };
  const profileCardLabelProps: TextProps = {
    fz: "sm",
    c: theme === "dark" ? Colors.WhiteBlueTransparent : Colors.GraySecondary,
    fw: 500,
  };
  const profileCardValueProps: TextProps = {
    fz: "sm",
    c: theme === "dark" ? Colors.GreenPrimary : Colors.GreenSecondary,
    fw: 500,
    ml: 5,
  };
  const tabTitleProps: TextProps = {
    fz: "md",
    c: theme === "dark" ? Colors.WhiteBlueTransparent : Colors.GreenSecondary,
    fw: 500,
    mb: 10,
  };
  const tableRowHeadProps: TableThProps = {
    style: {
      backgroundColor: theme === "dark" ? "transparent" : Colors.White,
      color: theme === "dark" ? Colors.White : Colors.BlackPrimary,
      borderRight: "1px solid #E0E0E0",
    },
  };
  return (
    <Container size="md">
      {context ? (
        <>
          {context.isLoadingProfile ? (
            <MegaLoader />
          ) : (
            <>
              {context.profile?.isPromoter ? (
                <div className="promoter-container">
                  <br />
                  <Tabs
                    variant={theme === "dark" ? "outline" : "default"}
                    defaultValue="profile"
                    color={Colors.GreenPrimary}
                  >
                    <Tabs.List grow mb={20}>
                      <Tabs.Tab value="profile">
                        <IconUser
                          color={Colors.GreenSecondary}
                          size={22}
                          strokeWidth={1.7}
                        />
                      </Tabs.Tab>
                      <Tabs.Tab value="transactions">
                        <IconReceipt
                          color={Colors.GreenSecondary}
                          size={22}
                          strokeWidth={1.7}
                        />
                      </Tabs.Tab>
                      <Tabs.Tab value="payouts">
                        <IconCurrencyDollar
                          color={Colors.GreenSecondary}
                          size={22}
                          strokeWidth={1.7}
                        />
                      </Tabs.Tab>
                      <Tabs.Tab value="leaderboard">
                        <IconTimeline
                          color={Colors.GreenSecondary}
                          size={22}
                          strokeWidth={1.7}
                        />
                      </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="profile">
                      <Text {...tabTitleProps}>Promoter Details</Text>

                      {isLoadingPromoter ? (
                        <MegaLoader />
                      ) : (
                        <Card
                          maw={400}
                          padding={0}
                          className={`profile-card profile-card-${theme}`}
                        >
                          <div className="flex-col align-center width-100">
                            <div className="initials flex-row align-center justify-center">
                              <Text className="text">
                                {context?.profile?.name[0]}
                              </Text>
                            </div>
                            <Text
                              fw={700}
                              fz={20}
                              mb={20}
                              c={
                                theme === "dark"
                                  ? Colors.White
                                  : Colors.BlackPrimary
                              }
                            >
                              {context.profile.name}
                            </Text>
                          </div>

                          <div className="row flex-row align-center">
                            <Text {...profileCardLabelProps}>Email:</Text>

                            <Text {...profileCardValueProps}>
                              {context.profile.email}
                            </Text>
                          </div>
                          <div className="row flex-row align-center">
                            <Text {...profileCardLabelProps}>
                              Promotion Date:
                            </Text>

                            <Text {...profileCardValueProps}>
                              {dayjs(promoter?.data.createdAt ?? "").format(
                                "ddd, DD MMM YYYY"
                              )}
                            </Text>
                          </div>
                          <div className="row flex-row align-center">
                            <Text {...profileCardLabelProps}>Code:</Text>

                            <CopyButton value={promoter?.data?.coupon ?? ""}>
                              {({ copied, copy }) => (
                                <Tooltip
                                  label={
                                    copied ? "Copied!" : "Copy to clipboard"
                                  }
                                >
                                  <Badge
                                    fz="sm"
                                    onClick={copy}
                                    style={{
                                      cursor: "pointer",
                                    }}
                                    ml={5}
                                    fw={600}
                                    size="lg"
                                    variant="light"
                                    color={
                                      copied
                                        ? Colors.GreenPrimary
                                        : Colors.BluePrimary
                                    }
                                  >
                                    {promoter?.data?.coupon}
                                  </Badge>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </div>
                          <div className="row flex-row align-center">
                            <Text {...profileCardLabelProps}>Status:</Text>

                            <Text
                              fz="sm"
                              ml={5}
                              fw={600}
                              c={
                                promoter?.data?.active
                                  ? Colors.GreenSecondary
                                  : Colors.RedPrimary
                              }
                            >
                              {promoter?.data?.active ? "Active" : "Inactive"}
                            </Text>
                          </div>
                          <div className="row flex-row align-center">
                            <Text {...profileCardLabelProps}>
                              Lifetime Earnings:
                            </Text>

                            <Text {...profileCardValueProps}>
                              $
                              {getFinancialValueFromNumeric(
                                promoter?.data?.totalEarnings
                              )}
                            </Text>
                          </div>
                          <div className="row flex-row align-center">
                            <Text {...profileCardLabelProps}>
                              Current Balance:
                            </Text>

                            <Text {...profileCardValueProps}>
                              $
                              {getFinancialValueFromNumeric(
                                promoter?.data?.balance
                              )}
                            </Text>
                          </div>
                        </Card>
                      )}
                    </Tabs.Panel>
                    <Tabs.Panel value="transactions">
                      <Text {...tabTitleProps}>Transactions</Text>
                      {currentTransaction && (
                        <Modal
                          opened={isTransactionModalOpened}
                          onClose={() => {
                            setCurrentTransaction(undefined);
                            CloseTransactionModal();
                          }}
                          title="Transaction Details"
                          centered
                          size="lg"
                          padding={0}
                          radius="lg"
                          withCloseButton={true}
                          styles={{
                            header: {
                              backgroundColor:
                                theme === "dark"
                                  ? "var(--mantine-color-dark-7)"
                                  : Colors.White,
                              color:
                                theme === "dark"
                                  ? Colors.White
                                  : Colors.BlackPrimary,
                              padding: "20px 24px",
                              borderBottom: `1px solid ${
                                theme === "dark"
                                  ? "var(--mantine-color-dark-6)"
                                  : "var(--mantine-color-gray-2)"
                              }`,
                            },
                            body: {
                              padding: 0,
                            },
                            content: {
                              overflow: "hidden",
                            },
                          }}
                        >
                          <div
                            className={`transaction-modal transaction-modal-${theme}`}
                          >
                            <Table
                              variant="vertical"
                              layout="fixed"
                              withTableBorder
                            >
                              <Table.Thead>
                                <Table.Tr key={generateRandomString(12)}>
                                  <Table.Th {...tableRowHeadProps} w={140}>
                                    Code
                                  </Table.Th>
                                  <Table.Td>
                                    <CopyButton
                                      value={currentTransaction.coupon.code}
                                    >
                                      {({ copied, copy }) => (
                                        <Tooltip
                                          label={
                                            copied
                                              ? "Copied!"
                                              : "Copy to clipboard"
                                          }
                                        >
                                          <div
                                            className="pointer flex-row align-center"
                                            onClick={copy}
                                            style={{
                                              color: copied
                                                ? Colors.GreenPrimary
                                                : Colors.BluePrimary,
                                              height: "100%",
                                            }}
                                          >
                                            {currentTransaction.coupon.code}
                                          </div>
                                        </Tooltip>
                                      )}
                                    </CopyButton>
                                  </Table.Td>
                                </Table.Tr>
                                <Table.Tr key={generateRandomString(12)}>
                                  <Table.Th {...tableRowHeadProps}>
                                    Discount
                                  </Table.Th>
                                  <Table.Td
                                    c={
                                      theme === "dark"
                                        ? Colors.White
                                        : Colors.GraySecondary
                                    }
                                  >
                                    {currentTransaction.coupon.discount}%
                                  </Table.Td>
                                </Table.Tr>
                                <Table.Tr key={generateRandomString(12)}>
                                  <Table.Th {...tableRowHeadProps}>
                                    Date
                                  </Table.Th>
                                  <Table.Td
                                    c={
                                      theme === "dark"
                                        ? Colors.White
                                        : Colors.BlackPrimary
                                    }
                                  >
                                    {dayjs(currentTransaction.createdAt).format(
                                      "DD MMM YYYY - hh:mmA"
                                    )}
                                  </Table.Td>
                                </Table.Tr>
                                <Table.Tr key={generateRandomString(12)}>
                                  <Table.Th {...tableRowHeadProps}>
                                    Earnings
                                  </Table.Th>
                                  <Table.Td
                                    fw={600}
                                    c={Colors.GreenPrimary}
                                    fz={17}
                                  >
                                    $
                                    {getFinancialValueFromNumeric(
                                      getOriginalValueFromDiscount(
                                        currentTransaction.amount,
                                        currentTransaction.coupon.discount
                                      )
                                    )}
                                  </Table.Td>
                                </Table.Tr>
                                <Table.Tr key={generateRandomString(12)}>
                                  <Table.Th {...tableRowHeadProps}>
                                    Service(s)
                                  </Table.Th>
                                  <Table.Td>
                                    <div className="flex-row align-center temp-service-images">
                                      {currentTransaction.services.map((s) => (
                                        <img
                                          src={BrandImages.retrieveImage(
                                            s,
                                            theme
                                          )}
                                          alt=""
                                          className="transaction-image-row"
                                        />
                                      ))}
                                    </div>
                                  </Table.Td>
                                </Table.Tr>
                              </Table.Thead>
                            </Table>
                            <Button
                              color={Colors.GreenPrimary}
                              onClick={() => {
                                CloseTransactionModal();
                                setCurrentTransaction(undefined);
                              }}
                              w={120}
                              mt={20}
                            >
                              Close
                            </Button>
                          </div>
                        </Modal>
                      )}
                      <Grid gutter={4}>
                        {transactions?.data.map((transaction) => {
                          return (
                            <Grid.Col
                              span={{ base: 12, sm: 6, lg: 6 }}
                              key={transaction.id}
                            >
                              <Paper
                                onClick={() => {
                                  setCurrentTransaction(transaction);
                                  OpenTransactionModal();
                                }}
                                withBorder
                                className={`transaction transaction-${theme} pointer`}
                              >
                                <div className="content flex-col justify-between width-100">
                                  <div className="flex-row align-center justify-between width-100">
                                    <div className="flex-col">
                                      <Text
                                        c={
                                          theme === "dark"
                                            ? Colors.WhiteBlueTransparent
                                            : Colors.GrayPrimary
                                        }
                                        mb={-10}
                                        fz={14}
                                      >
                                        Discount Value
                                      </Text>
                                      <Text
                                        fw={500}
                                        fz={19}
                                        w="fit-content"
                                        c={Colors.BluePrimary}
                                      >
                                        {transaction.coupon.discount}%
                                      </Text>
                                    </div>
                                    <div className="flex-col align-end">
                                      <Text
                                        c={
                                          theme === "dark"
                                            ? Colors.WhiteBlueTransparent
                                            : Colors.GraySecondary
                                        }
                                        mb={0}
                                        fz={14}
                                      >
                                        {dayjs(transaction.createdAt).format(
                                          "ddd, DD MMM YYYY"
                                        )}
                                      </Text>
                                      <Text c={Colors.GrayPrimary} fz={14}>
                                        <b>at</b>&nbsp;
                                        {dayjs(transaction.createdAt).format(
                                          "hh:mm A"
                                        )}
                                      </Text>
                                    </div>
                                  </div>
                                  <div className="flex-row align-center width-100 justify-between">
                                    <Text
                                      fw={500}
                                      c={Colors.GreenPrimary}
                                      fz={20}
                                    >
                                      $
                                      {getFinancialValueFromNumeric(
                                        getOriginalValueFromDiscount(
                                          transaction.amount,
                                          transaction.coupon.discount
                                        )
                                      )}
                                    </Text>
                                    <div className="flex-row align-center">
                                      {transaction.services.map((s, index) => (
                                        <img
                                          key={index}
                                          src={BrandImages.retrieveImage(
                                            s,
                                            theme
                                          )}
                                          alt=""
                                          className="transaction-image-row"
                                        />
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </Paper>
                            </Grid.Col>
                          );
                        })}
                      </Grid>
                      <div className="flex-row align-center justify-center width-100">
                        <Pagination
                          value={transactionsPagination.page}
                          onChange={(value) => {
                            setTransactionsPagination({
                              ...transactionsPagination,
                              page: value,
                            });
                          }}
                          my={40}
                          disabled={isLoadingTransactions}
                          total={getPaginationTotal(
                            transactions?.totalRecords,
                            transactionsPagination.limit
                          )}
                        />
                      </div>
                    </Tabs.Panel>
                    <Tabs.Panel value="payouts">
                      <div className="flex-row align-center justify-between width-100">
                        <Text {...tabTitleProps}>Payout History</Text>
                        <Button
                          color={Colors.GreenPrimary}
                          mb={10}
                          onClick={RequestPayout}
                          loading={isRequestingPayout}
                          display="none"
                        >
                          Request Payout
                        </Button>
                      </div>
                      {isLoadingPayouts ? (
                        <MegaLoader />
                      ) : (
                        <>
                          {payouts && payouts.data && (
                            <Grid gutter={4}>
                              {payouts?.data.map((payout) => {
                                return (
                                  <Grid.Col
                                    span={{ base: 12, sm: 6, lg: 6 }}
                                    key={payout.id}
                                  >
                                    <Paper
                                      withBorder
                                      className={`transaction transaction-${theme}`}
                                    >
                                      <div className="content flex-col justify-between width-100">
                                        <div className="flex-row  justify-between width-100">
                                          <div className="flex-col">
                                            <Text
                                              c={Colors.GrayPrimary}
                                              mb={-10}
                                              fz={14}
                                            >
                                              Status
                                            </Text>
                                            <Text
                                              fw={600}
                                              fz={15}
                                              mt={4}
                                              tt="uppercase"
                                              w="fit-content"
                                              c={
                                                payout.status === "pending"
                                                  ? Colors.OrangePrimary
                                                  : Colors.GreenPrimary
                                              }
                                            >
                                              {payout.status}
                                            </Text>
                                          </div>
                                          <div className="flex-col align-end">
                                            <Text
                                              c={Colors.GrayPrimary}
                                              mb={-10}
                                              fz={14}
                                            >
                                              Payout Date
                                            </Text>
                                            <Text
                                              mt={5}
                                              c={
                                                theme === "dark"
                                                  ? Colors.WhiteBlueTransparent
                                                  : Colors.GraySecondary
                                              }
                                              fz={14}
                                            >
                                              {dayjs(payout.createdAt).format(
                                                "ddd, DD MMM YYYY"
                                              )}
                                            </Text>
                                            <Text
                                              c={Colors.GrayPrimary}
                                              fz={14}
                                            >
                                              <b>at</b>
                                              &nbsp;
                                              {dayjs(payout.createdAt).format(
                                                "hh:mm A"
                                              )}
                                            </Text>
                                          </div>
                                        </div>
                                        <div className="flex-row align-center width-100 justify-between">
                                          <Text
                                            fw={500}
                                            c={Colors.GreenPrimary}
                                            fz={20}
                                          >
                                            $
                                            {getFinancialValueFromNumeric(
                                              payout.amount
                                            )}
                                          </Text>
                                          <div className="flex-row align-center"></div>
                                        </div>
                                      </div>
                                    </Paper>
                                  </Grid.Col>
                                );
                              })}
                            </Grid>
                          )}
                        </>
                      )}
                    </Tabs.Panel>
                    <Tabs.Panel value="leaderboard">
                      <Text {...tabTitleProps}>Promoters Leaderboard</Text>
                      <Table
                        borderColor={
                          theme === "dark" ? Colors.White : Colors.GrayTertiary
                        }
                      >
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th
                              c={
                                theme === "dark"
                                  ? Colors.WhiteBlueTransparent
                                  : Colors.BlackPrimary
                              }
                            >
                              Rank
                            </Table.Th>
                            <Table.Th
                              c={
                                theme === "dark"
                                  ? Colors.WhiteBlueTransparent
                                  : Colors.BlackPrimary
                              }
                            >
                              Promoter
                            </Table.Th>
                            <Table.Th
                              c={
                                theme === "dark"
                                  ? Colors.WhiteBlueTransparent
                                  : Colors.BlackPrimary
                              }
                            >
                              Earnings
                            </Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {isLoadingLeaderboard ? (
                            <div className="flex-row align-center justify-center">
                              <MegaLoader />
                            </div>
                          ) : (
                            <>
                              {leaderboard && leaderboard.data && (
                                <>
                                  {leaderboard.data.map((p, index) => {
                                    return (
                                      <Table.Tr
                                        key={p._id}
                                        bg={
                                          context?.profile?.email === p.email
                                            ? Colors.GreenLight
                                            : theme === "dark"
                                            ? Colors.BlackPrimary
                                            : Colors.White
                                        }
                                        c={
                                          context?.profile?.email === p.email
                                            ? Colors.BlackPrimary
                                            : theme === "dark"
                                            ? Colors.WhiteBlueTransparent
                                            : Colors.BlackPrimary
                                        }
                                      >
                                        <Table.Td>{index + 1}</Table.Td>
                                        <Table.Td>
                                          {maskEmail(p.email)}
                                        </Table.Td>
                                        <Table.Td
                                          c={Colors.GreenSecondary}
                                          fw={500}
                                        >
                                          $
                                          {getFinancialValueFromNumeric(
                                            p.totalEarnings
                                          )}
                                        </Table.Td>
                                      </Table.Tr>
                                    );
                                  })}
                                </>
                              )}
                            </>
                          )}
                        </Table.Tbody>
                      </Table>
                      <div className="flex-row align-center justify-center width-100">
                        <Pagination
                          value={promotersPagination.page}
                          onChange={(value) => {
                            setPromotersPagination({
                              ...promotersPagination,
                              page: value,
                            });
                          }}
                          my={40}
                          disabled={isLoadingTransactions}
                          total={getPaginationTotal(
                            transactions?.totalRecords,
                            promotersPagination.limit
                          )}
                        />
                      </div>
                    </Tabs.Panel>
                  </Tabs>
                </div>
              ) : (
                <DisplayAlert
                  title="No Subscriptions Found"
                  children={
                    <div className="flex-col">
                      <Text>You are not authorized to view this :-(</Text>
                      <Link to="/d/services">
                        <Button mt={10} color={Colors.GreenPrimary}>
                          Back to Services
                        </Button>
                      </Link>
                    </div>
                  }
                />
              )}
            </>
          )}
        </>
      ) : (
        <MegaLoader />
      )}
    </Container>
  );
};

export default PromoterScreen;
